import { CommonQueryKeys } from '~/common/api';
import {
  OverridableQuery,
  restrictApi,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import { IBrand, IBrandPaginationQuery } from '~/common/types';

type Options = {
  list?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IBrand[]>, IErrorApiResponse>
  >;
  paginate?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IBrand[]>, IErrorApiResponse> & {
      args: IBrandPaginationQuery;
    }
  >;
};

export const useManageBrands = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi } = useApi();

  const listBrands = useQuery({
    queryKey: CommonQueryKeys.brands.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IBrand[]>>('/settings/api/brands'),
    ...options.list,
    enabled: Boolean(options.list?.enabled),
  });

  const paginateBrands = useQuery({
    queryKey: CommonQueryKeys.brands.paginate(
      options.paginate?.args as IBrandPaginationQuery,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IBrand[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/brands`,
        {
          method: 'post',
          body: JSON.stringify(options.paginate?.args),
        },
      ),
    ...options.paginate,
    enabled: Boolean(options.paginate?.enabled && options.paginate.args),
  });

  return restrictApi(options, { list: listBrands, paginate: paginateBrands });
};
